import { useCallback } from 'react';
import { Editor, Transforms, Range } from 'slate';
import { useSlashCommands } from '../slash-commands/use-slash-commands';
import { SlashCommand } from '../slash-commands/slash-commands-config';

interface UseSlashCommandHandlingReturn {
  isOpen: boolean;
  commandText: string;
  targetRange: Range | null;
  handleKeyDown: (event: React.KeyboardEvent) => void;
  handleCommand: (command: SlashCommand) => void;
  decorate: (entry: [any, number[]]) => any[];
  closeMenu: () => void;
}

/**
 * Custom hook to manage slash commands in the Slate editor.
 * Implements command palette functionality following Slate's conventions.
 *
 * @param editor - Slate Editor instance
 * @returns Object containing slash command state and handlers
 */
export function useSlashCommandHandling(editor: Editor): UseSlashCommandHandlingReturn {
  const { isOpen, commandText, targetRange, onKeyDown, closeMenu, decorate } =
    useSlashCommands(editor);

  /**
   * Handles slash command execution using Slate's transformation system.
   * Follows Slate's conventions for text deletion and range manipulation.
   *
   * @param command - The slash command to execute
   */
  const handleCommand = useCallback(
    (command: SlashCommand) => {
      if (targetRange) {
        // Include the slash in the deletion range
        const commandLength = commandText.length + 1;

        // Create a range that covers the entire command text
        const deleteRange = {
          anchor: targetRange.anchor,
          focus: {
            path: targetRange.anchor.path,
            offset: targetRange.anchor.offset + commandLength
          }
        } as Range;

        // Use Slate's transformation system to remove the command text
        Transforms.delete(editor, { at: deleteRange });
      }

      // Close the command menu before executing the command
      closeMenu();
      command.action();
    },
    [closeMenu, editor, targetRange, commandText]
  );

  /**
   * Handles keyboard events for slash command interaction.
   * Delegates to the slash command system's keyboard handler.
   *
   * @param event - Keyboard event to handle
   */
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      onKeyDown(event);
    },
    [onKeyDown]
  );

  return {
    isOpen,
    commandText,
    targetRange,
    handleKeyDown,
    handleCommand,
    decorate,
    closeMenu
  };
}
